/// <amd-module name="Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownViewComponent" />
import { AnalyticsExceptionType, extractODataError, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { WidgetPresentationMode } from "Dashboards/WidgetContracts/WidgetProps";
import { LoadingStatus } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import {
    ComponentLayoutMapping,
    MinimalChartAndLightBoxAndReportComponentLayouts
} from "Reporting/Visuals/LayoutComponents/Widgets/ComponentLayoutEngine";
import { MessageType, packMessageAsState } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutState";
import { CompactChartErrorWarningComponent } from "Reporting/Visuals/VisualMessages/CompactChartErrorWarningComponent";
import { ActionCreatorBase } from "WidgetCommon/ModernWidgetTypes/ActionCreatorBase";
import { SettingsManagerBase } from "WidgetCommon/ModernWidgetTypes/SettingsManagerBase";
import { ViewComponentBase, ViewComponentProps } from "WidgetCommon/ModernWidgetTypes/ViewComponentBase";
import { WidgetDataManagerBase, WidgetDataManagerOptions } from "WidgetCommon/ModernWidgetTypes/WidgetDataManagerBase";
import { MessageOptions } from "WidgetCommon/ModernWidgetTypes/WidgetMessageCardFactory";
import { AnalyticsSprintBurndownSettings } from "Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownSettings";
import * as Resources from "Widgets/AnalyticsSprintBurndown/Resources";
import { BurndownActionCreator } from "Widgets/Burndown/BurndownWidgetComponents/BurndownActionCreator";
import { getMessageOptions, packWidgetLoadedTelemetryData } from "Widgets/Burndown/BurndownWidgetComponents/BurndownViewComponent";
import { AnalyticsSprintBurndownDataManager } from "./AnalyticsSprintBurndownDataManager";
import { AnalyticsSprintBurndownSettingsManager, convertSprintBurndownToBurndownSettings } from "./AnalyticsSprintBurndownSettingsManager";

export interface AnalyticsSprintBurndownViewComponentProps extends ViewComponentProps {}

export default class AnalyticsSprintBurndownViewComponent extends ViewComponentBase<
    AnalyticsSprintBurndownViewComponentProps,
    AnalyticsSprintBurndownSettings
> {
    protected createDataManager(options: WidgetDataManagerOptions): WidgetDataManagerBase {
        const isMinimalChart = this.isMinimalChart();
        const isLightboxChart = this.isLightboxChart();
        const commandName = this.computeCommandName();
        return new AnalyticsSprintBurndownDataManager(
            this.context.pageContext,
            this.props.teamContext,
            options,
            !isMinimalChart && !isLightboxChart, // Chart clicks should be allowed except in header chart and lightbox cases
            commandName,
            isMinimalChart // Used to indicate that user behavior is not driving queries in this scenario.
        );
    }

    /** Describes the set of layout mappings supported by this widget. */
    protected getLayoutMappings(): ComponentLayoutMapping[] {
        return MinimalChartAndLightBoxAndReportComponentLayouts.getDefaultMappings();
    }

    protected createActionCreator(): ActionCreatorBase {
        return new BurndownActionCreator(
            this.context.pageContext,
            this.dataManager,
            this.store,
            this.actions,
            this.props.contributionId,
            extractODataError
        );
    }

    protected createSettingsManager(): SettingsManagerBase<AnalyticsSprintBurndownSettings> {
        return new AnalyticsSprintBurndownSettingsManager(this.context.pageContext, this.props.teamContext);
    }

    protected getMessageOptions(): MessageOptions | undefined {
        return getMessageOptions(this.context.pageContext, this.getStoreState(), this.props);
    }

    protected renderMessageCard(): JSX.Element | null {
        if (this.isMinimalChart()) {
            return <CompactChartErrorWarningComponent />;
        }
        return super.renderMessageCard();
    }

    protected handleErrorOnRenderCompletion(error: Error): void {
        if (this.isMinimalChart()) {
            this.props.onRenderCompletion({
                outcome: LoadingStatus.success
            });
            return;
        }
        super.handleErrorOnRenderCompletion(error);
    }

    protected handleSettingsError(error: Error) {
        if ((error.message as string).indexOf(Resources.IterationDatesUnconfiguredMessage) !== -1) {
            this.store.setState(packMessageAsState(MessageType.SetIterationDates));
            this.setStoreStateAndRenderCompletion();
            return;
        }

        if (recognizeAnalyticsException(error) === AnalyticsExceptionType.MissingAnalyticsPermissions) {
            this.store.setState(packMessageAsState(MessageType.MissingPermissions));
            this.setStoreStateAndRenderCompletion();
            return;
        }

        super.handleSettingsError(error);
    }

    /** Describes if the widget should render in a minimal form  */
    protected isMinimalChart(): boolean | undefined {
        return this.props && this.props.settings && JSON.parse(this.props.settings).isMinimalChart;
    }

    /** Describes if the widget should render in a lightbox form  */
    protected isLightboxChart(): boolean | undefined {
        return this.props && this.props.settings && JSON.parse(this.props.settings).isLightboxChart;
    }
    /** Pass custom telemetry about the load event for Standardized Widget Reporting. */
    protected packWidgetLoadedTelemetryData(analyticsSprintBurndownSettings: AnalyticsSprintBurndownSettings): { [key: string]: any } {
        if (!analyticsSprintBurndownSettings) {
            return { settingsWereEmpty: true };
        }
        try {
            const burndownSettings = convertSprintBurndownToBurndownSettings(analyticsSprintBurndownSettings);
            const telemetryData = packWidgetLoadedTelemetryData(this.context.pageContext, burndownSettings);

            // Extend the Release burndown telemetry with Sprint Burndown specific details
            telemetryData.showNonWorkingDays = analyticsSprintBurndownSettings.showNonWorkingDays;
            telemetryData.isCustomized = analyticsSprintBurndownSettings.isCustomized;
            telemetryData.isLegacy = analyticsSprintBurndownSettings.isLegacy;
            telemetryData.isMinimal = this.isMinimalChart() ? true : false;
            telemetryData.isLightbox = this.isLightboxChart() ? true : false;

            return telemetryData;
        } catch {
            return { settingsCouldNotBePacked: true };
        }
    }

    private computeCommandName(): string {
        return this.props.presentationMode === WidgetPresentationMode.embeddedView
            ? this.isMinimalChart()
                ? "HeaderSprintBurndownReport"
                : "SprintBurndownReport"
            : "SprintBurndown";
    }
}
